import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/ui-extra";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, CheckCircle2, CalendarClock, ClipboardList, ArrowRight } from "lucide-react";

/**
 * Confirm a task drafted by ChatGPT. The draft is owned by the signed-in
 * user; confirming saves it to the app's tasks under their account.
 */
export default function TaskConfirmPage() {
  const [params] = useSearchParams();
  const draftId = params.get("draft");
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState({ title: "", task_type: "", due_at: "", notes: "" });
  const [horseName, setHorseName] = useState(null);
  const [alreadyConfirmed, setAlreadyConfirmed] = useState(false);

  useEffect(() => {
    if (!draftId) { setNotFound(true); setLoading(false); return; }
    (async () => {
      try {
        const res = await api.get(`/connector/task-drafts/${draftId}`);
        const d = res.data || {};
        setForm({
          title: d.title || "",
          task_type: d.task_type || "general",
          due_at: d.due_at || "",
          notes: d.notes || "",
        });
        setHorseName(d.horse_name || null);
        if (d.status === "confirmed") setAlreadyConfirmed(true);
      } catch (_e) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [draftId]);

  const confirm = async () => {
    if (!form.title.trim()) { toast.error("A task title is required"); return; }
    setSaving(true);
    try {
      await api.post(`/connector/task-drafts/${draftId}/confirm`, form);
      setSaved(true);
      toast.success("Task saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save task");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="py-20 text-center text-muted-foreground" data-testid="task-confirm-loading"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;
  }

  if (notFound) {
    return (
      <div data-testid="task-confirm-notfound">
        <PageHeader eyebrow="Tasks" title="Task not found" subtitle="This draft link is invalid or belongs to another account." />
        <Button asChild variant="outline"><Link to="/tasks">Go to Tasks</Link></Button>
      </div>
    );
  }

  if (saved || alreadyConfirmed) {
    return (
      <div data-testid="task-confirm-done">
        <PageHeader eyebrow="Tasks" title={alreadyConfirmed && !saved ? "Already saved" : "Task saved"} />
        <Card className="rounded-xl max-w-lg">
          <CardContent className="pt-6 text-center space-y-4">
            <CheckCircle2 className="h-12 w-12 text-[hsl(var(--ap-forest))] mx-auto" />
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
              {alreadyConfirmed && !saved
                ? "This task was already confirmed and saved to your tasks."
                : "Your task has been recorded under your account."}
            </p>
            <div className="flex gap-2 justify-center">
              <Button onClick={() => navigate("/tasks")} data-testid="task-confirm-view-tasks"
                className="bg-[hsl(var(--ap-forest))] hover:bg-[hsl(var(--ap-forest))]/90">
                View my tasks <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="task-confirm-page">
      <PageHeader
        eyebrow="Tasks · from ChatGPT"
        title="Confirm this task"
        subtitle="ChatGPT prepared this. Review it, make any edits, and save it to your tasks."
      />
      <Card className="rounded-xl max-w-lg">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-[hsl(var(--ap-forest))]" /> Task details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs">Title</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-confirm-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Type</Label>
              <Input value={form.task_type} onChange={(e) => setForm({ ...form, task_type: e.target.value })} placeholder="vet, farrier, general…" data-testid="task-confirm-type" />
            </div>
            <div>
              <Label className="text-xs">Due</Label>
              <Input value={form.due_at} onChange={(e) => setForm({ ...form, due_at: e.target.value })} placeholder="e.g. tomorrow, 2026-05-01" data-testid="task-confirm-due" />
            </div>
          </div>
          {horseName && (
            <div className="text-xs text-muted-foreground">Linked horse: <strong>{horseName}</strong></div>
          )}
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} data-testid="task-confirm-notes" />
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" onClick={() => navigate("/tasks")} data-testid="task-confirm-cancel">Cancel</Button>
            <Button onClick={confirm} disabled={saving} data-testid="task-confirm-save"
              className="bg-[hsl(var(--ap-forest))] hover:bg-[hsl(var(--ap-forest))]/90">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ClipboardList className="h-4 w-4 mr-1" />}
              Confirm &amp; save
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
